import axios from "axios";
import { CUSTOMER_MANAGEMENT_SERVICE_ADDRESS } from "constants/app.constants";

/**
 * A private axios instance for customer-management, deliberately NOT the global
 * one every other EVA service call rides.
 *
 * Two things follow from an instance, and both are the point rather than tidiness:
 *
 *  - **EVA's global interceptors do not run on it.** `App.tsx` installs a
 *    response interceptor that treats *any* 401 as EVA's own session expiring:
 *    in bridge mode it re-mints via `/current` and, failing that, calls
 *    `window.location.reload()`. customer-management is a different service on a
 *    different host and its 401 says nothing about EVA's session, so letting it
 *    reach that handler would let an unrelated service reload the user's page —
 *    or, with a retry, loop. Both endpoints below sit under `/users/current`,
 *    which the guard happens to skip by substring today; an instance removes the
 *    coupling instead of depending on that.
 *  - **EVA's bearer token is not attached.** `authSlice` sets
 *    `axios.defaults.headers.common.Authorization` on the global instance. This
 *    service is on the Novi apex, a different host, and EVA's JWT is not its
 *    credential — it authenticates by the Novi session cookie, which is scoped
 *    to the apex and so is sent to it by `withCredentials`. An instance
 *    snapshots `axios.defaults` when it is created, so a token set after this
 *    module loads never reaches it; the delete below covers the one remaining
 *    case, this module being imported after a session already exists, so no
 *    import order can send EVA's token to another origin.
 */
// No X-Requested-With: from the legacy host this call is cross-origin and a
// custom header would force a preflight; the /api/ segment classifies it anyway.
const customerManagementApi = axios.create({ withCredentials: true });
delete customerManagementApi.defaults.headers.common.Authorization;

/**
 * Where customer-management is reached from this page. A root-relative address
 * is same-origin on a customer host and in local dev (Vite serves a stub). The
 * legacy `eva.<apex>` host is the exception: its edge forwards every path to
 * EVA's own origin, which has no route to customer-management, so from there
 * the call goes to the apex directly, cross-origin, which the apex CORS
 * allowance for that origin covers. An absolute address is used as given.
 */
export function customerManagementBase(
  configured: Maybe<string> = CUSTOMER_MANAGEMENT_SERVICE_ADDRESS,
  loc: { protocol: string; hostname: string } = window.location
): Maybe<string> {
  if (!configured || /^https?:/i.test(configured)) return configured;
  const legacyEvaHost = /^eva\.(.+)$/i.exec(loc.hostname);
  return legacyEvaHost ? `${loc.protocol}//${legacyEvaHost[1]}${configured}` : configured;
}

/**
 * One row of customer-management's application-to-permission table.
 *
 * `application` is the app's key (`intelligence-ma`), `permission` the key a
 * user needs to reach it (`ACCESS_INTELLIGENCE_MA`), and `path` where it is
 * served — null for a product routed by subdomain rather than under the apex.
 *
 * This table is the reason a front end does not have to hardcode a permission
 * name, and hardcoding one would be wrong: the names do not follow from the
 * application keys. `insights` is gated by ACCESS_INSIGHT_ENGINE, not
 * ACCESS_INSIGHTS, and `intelligence-ma` was ACCESS_MA_COMPS until a migration
 * renamed it.
 */
export interface ApplicationPermissionMapping {
  application: string;
  permission: string;
  path: Nullable<string>;
}

/** The part of the `/users/current` payload the app switcher reads. */
export interface CurrentUser {
  /** e.g. ACCESS_INTELLIGENCE_APP, ACCESS_INSIGHT_ENGINE. */
  permissions: string[];
  /**
   * The customer this lookup was answered for, or null when it was answered
   * without one. EVA always asks the customer-less way, so this is null in
   * practice; it is read rather than assumed because the service may start
   * selecting one and the answer should win over the fallback below.
   */
  customer: Nullable<string>;
  /** Every customer this user is affiliated with, e.g. `["novi"]`. */
  customers: string[];
}

/**
 * A single DNS label, which is all a customer may ever be.
 *
 * This is a security boundary, not tidiness. The customer becomes the first
 * label of a hostname, so any character that can end a host lets the value
 * choose its own: `"evil.com/"` turns `https://<customer>.<apex>/x` into
 * `https://evil.com/.<apex>/x`, and `#`, `?`, `\` and `user@` do the same.
 * A label admits none of them.
 */
const CUSTOMER_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/** Whether `value` is usable as the customer half of a Novi hostname. */
export const isCustomerLabel = (value: unknown): value is string =>
  typeof value === "string" && CUSTOMER_LABEL.test(value);

/**
 * The customer whose subdomain the other Novi apps are served from.
 *
 * `customer` when the service named one, otherwise the first affiliation. The
 * fallback is a guess for a user affiliated with more than one customer, and
 * `null` is the honest answer when there is nothing to guess from — see
 * {@link reachableProducts}, where that means linking to the apex and letting
 * Novi route or offer its own selector.
 *
 * Every field is checked rather than trusted. `fetchCurrentUser` normalises the
 * payload, but this is exported and the values originate in a service EVA does
 * not own, so a null `customers`, a list holding something that is not a string,
 * or a name that is not a DNS label all have to mean "no customer" rather than
 * throw or hand a hostname to a value that can rewrite it.
 */
export const effectiveCustomer = (user: Maybe<CurrentUser>): Nullable<string> => {
  if (isCustomerLabel(user?.customer)) return user.customer;

  const first = Array.isArray(user?.customers) ? user.customers[0] : undefined;
  return isCustomerLabel(first) ? first : null;
};

/**
 * The Novi apex host, e.g. `dev-novilabs.io`, taken from the customer-management
 * address rather than a variable of its own.
 *
 * One source of truth, and a safe one: the current-user lookup is served on the
 * apex and nowhere else, so whatever host that address names *is* the apex.
 *
 * Null when the resolved address is root-relative (a customer host, or local
 * dev): product links stay paths, which on a customer host is already the
 * unified URL.
 */
export const noviApexHost = (
  base: Maybe<string> = customerManagementBase()
): Nullable<string> => {
  if (!base) return null;

  try {
    return new URL(base).hostname;
  } catch {
    return null;
  }
};

/**
 * The whole application-to-permission table.
 *
 * A catalogue, not an answer about anybody: what a given user may reach comes
 * from crossing it with their own permissions. See {@link reachableProducts}.
 *
 * Throws on a failed call, which is what react-query's `queryFn` wants.
 */
export async function fetchApplications(): Promise<ApplicationPermissionMapping[]> {
  const { data } = await customerManagementApi.get<ApplicationPermissionMapping[]>(
    `${customerManagementBase()}/applications`
  );

  // A payload that is not a list is an answer this code cannot read, and
  // treating it as "no applications" would quietly empty the switcher rather
  // than hide it. Throwing sends it down the same path as a failed call.
  if (!Array.isArray(data)) {
    throw new Error("customer-management returned a non-list applications payload");
  }

  return data;
}

/**
 * The signed-in user: their permissions, and which customer's subdomain the
 * other Novi apps are served from for them.
 *
 * Answered without a selected customer, which is why this is called on the apex
 * (`novilabs.io`) rather than a customer subdomain: EVA has no customer in its
 * host to offer, and `eva.<apex>` has a handler of its own that would never
 * forward this. The Novi cookie is scoped to the apex, so it reaches the
 * service from EVA's subdomain.
 *
 * `permissions` is normalised to an array here so no caller has to ask whether
 * the field was present. A payload without one is a user with no permissions,
 * which is a fact rather than a gap — but a payload that is not an object at
 * all is unreadable, and throws.
 */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  const { data } = await customerManagementApi.get<{
    permissions?: unknown;
    customer?: unknown;
    customers?: unknown;
  }>(`${customerManagementBase()}/users/current`);

  if (!data || typeof data !== "object") {
    throw new Error("customer-management returned an unreadable current-user payload");
  }

  return {
    permissions: Array.isArray(data.permissions) ? (data.permissions as string[]) : [],
    customer: typeof data.customer === "string" ? data.customer : null,
    // Filtered to strings so the declared type is not a lie: the service could
    // answer with a list holding anything.
    customers: Array.isArray(data.customers)
      ? data.customers.filter((c): c is string => typeof c === "string" && !!c)
      : []
  };
}
